import { useState } from "react";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Mail } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import freshorderLogo from "../assets/freshorder-logo.png";

export default function ClientPortal() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;

    setIsLoading(true);
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Server sets admin_session httpOnly cookie — no client-side auth flag needed
        sessionStorage.setItem("adminUser", JSON.stringify(data.user));

        // Store org info for API calls and branding
        if (data.organization) {
          sessionStorage.setItem("currentOrgSlug", data.organization.slug);
          sessionStorage.setItem("orgBranding", JSON.stringify(data.organization));
        }
        
        toast({
          title: "Login successful",
          description: `Welcome back, ${data.user.username}!`,
        });
        
        // B12 (M-A5): full-page nav on login, not SPA setLocation, so a fresh
        // TanStack Query cache is built for the org just logged into — a prior
        // tenant's cached data can't survive an org switch in the same tab.
        if (data.organization?.slug) {
          window.location.href = `/org/${data.organization.slug}/admin`;
        } else {
          window.location.href = "/admin";
        }
      } else {
        toast({
          title: "Login failed",
          description: data.message || "Invalid email or password",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Unable to connect. Please try again.",
        variant: "destructive"
      });
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img 
              src={freshorderLogo} 
              alt="FreshOrder" 
              className="h-16 mx-auto pt-[3px] pb-[3px] mt-[40px] mb-[40px]"
              data-testid="img-freshorder-logo"
            />
          </div>

          <Card className="shadow-lg border border-gray-200">
            <CardHeader className="text-center border-b border-gray-100 bg-gray-50/50 p-6">
              <div className="flex items-center justify-center gap-2 text-[#2B5397]">
                <Lock className="h-5 w-5" />
                <span className="font-semibold text-[16px]">Sign In</span>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-medium">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-12 bg-[rgb(243,244,246)]"
                      autoFocus
                      required
                      data-testid="input-email"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password" className="font-medium">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 h-12 bg-[rgb(243,244,246)]"
                      required
                      data-testid="input-password"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 text-lg bg-[#2B5397] hover:bg-[#3D8C40] text-white hover:text-white mt-2"
                  disabled={!email.trim() || !password || isLoading}
                  data-testid="button-sign-in"
                >
                  {isLoading ? "Signing in..." : "Sign In"}
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="mt-8 text-center space-y-3 pt-[10px] pb-[10px]">
            <p className="text-sm text-gray-600">
              Don't have an account?{" "}
              <a 
                href="https://freshorder.io#contact" 
                className="text-[#2B5397] hover:underline font-medium"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="link-contact-sales"
              >
                Contact Sales
              </a>
            </p>
          </div>
        </div>
      </div>
      <footer className="py-6 text-center text-sm text-gray-500 border-t border-gray-200 bg-white">
        <p>&copy; {new Date().getFullYear()} FreshOrder. All rights reserved.</p>
      </footer>
    </div>
  );
}
